import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Skeleton,
  Alert,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import SiteImage from "../components/SiteImage";

const AccountPage = () => {
  const { accessToken } = useAuth();
  const [user, setUser] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteDetails, setSiteDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetMessage, setResetMessage] = useState(null);
  
  // Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Password reset states
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordError, setPasswordError] = useState("");

  const baseURL = process.env.REACT_APP_BASEURL;

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch data in sequence: sites -> user -> site details
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Step 1: Fetch all sites
        const sitesRes = await axios.get(`${baseURL}/get-sites/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const fetchedSites = sitesRes.data.data || [];
        setSites(fetchedSites);

        // Step 2: Fetch current user id from the users list
        const userRes = await axios.get(`${baseURL}/users`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userSummary = userRes.data[0];
        if (!userSummary?.id) {
          throw new Error("No user found in /users response");
        }

        // Step 3: Fetch the full user record using the detail endpoint
        const userDetailRes = await axios.get(`${baseURL}/users/${userSummary.id}/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userData = userDetailRes.data;
        setUser(userData);
        setEditedUser({
          first_name: userData.first_name || "",
          last_name: userData.last_name || "",
          email: userData.email || ""
        });

        // Step 4: Fetch details for user's sites only
        const userSiteIds = userData.sites || [];
        const userSites = fetchedSites.filter((site) =>
          userSiteIds.includes(site.id)
        );
        const siteDetailsPromises = userSites.map((site) =>
          axios.get(`${baseURL}/sites/${site.id}/?with_image=true`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
        );
        const siteDetailsRes = await Promise.all(siteDetailsPromises);
        setSiteDetails(siteDetailsRes.map((res) => res.data));
      } catch (error) {
        console.error(
          "Error fetching data:",
          error.response ? error.response.data : error.message
        );
        setError(
          "Failed to load account or site data. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accessToken]);

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original values
      setEditedUser({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || ""
      });
    }
    setIsEditing(!isEditing);
  };

  // Handle input changes during editing
  const handleInputChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save user information
  const handleSaveUser = async () => {
    setIsUpdating(true);
    try {
      // Replace with your actual update endpoint
      const response = await axios.patch(
        `${baseURL}/users/${user.id}/`,
        editedUser,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      
      // Update local user state
      setUser(prev => ({
        ...prev,
        ...editedUser
      }));
      
      setIsEditing(false);
      setResetMessage({
        type: "success",
        text: "User information updated successfully."
      });
    } catch (error) {
      console.error("Error updating user:", error);
      setResetMessage({
        type: "error",
        text: "Failed to update user information. Please try again."
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle password reset button click
  const handleResetPasswordClick = () => {
    setShowPasswordFields(true);
    setResetMessage(null);
    setPasswordError("");
  };

  // Handle password reset form submission
  const handlePasswordSubmit = async () => {
    setPasswordError("");
    
    // Validate passwords
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError("Both password fields are required.");
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long.");
      return;
    }

    try {
      // Replace with your actual password reset endpoint
      await axios.post(
        `${baseURL}/users/change_password`,
        { new_password: passwordData.newPassword },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      
      setResetMessage({
        type: "success",
        text: "Password updated successfully."
      });
      
      // Reset form
      setShowPasswordFields(false);
      setPasswordData({ newPassword: "", confirmPassword: "" });
    } catch (error) {
      setResetMessage({
        type: "error",
        text: "Failed to update password. Please try again."
      });
    }
  };

  // Handle cancel password reset
  const handleCancelPasswordReset = () => {
    setShowPasswordFields(false);
    setPasswordData({ newPassword: "", confirmPassword: "" });
    setPasswordError("");
    setResetMessage(null);
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <Skeleton width="40%" />
        </Typography>
        <Paper sx={{ p: 3, mb: 4 }}>
          {Array(4)
            .fill()
            .map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                width="100%"
                height={56}
                sx={{ mb: 2 }}
              />
            ))}
          <Skeleton variant="rectangular" width={150} height={40} />
        </Paper>
      </Box>
      <Typography variant="h5" gutterBottom>
        <Skeleton width="30%" />
      </Typography>
      <Grid container spacing={3}>
        {Array(3)
          .fill()
          .map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton variant="rectangular" width={250} height={200}/>
              <Skeleton width="60%" sx={{ mt: 1 }} />
              <Skeleton width="80%" />
            </Grid>
          ))}
      </Grid>
    </Container>
  );

  if (loading) return <LoadingSkeleton />;

  if (error)
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );

  return (
    <Box sx={{ p: { xs: 0, sm: 0, md: 1, lg: 1 } }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}
      >
        Account
      </Typography>

      {/* User Information */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}
          >
            User Information
          </Typography>
          <Box>
            {isEditing ? (
              <>
                <Button variant="contained"
                  onClick={handleSaveUser} 
                  color="primary"
                  disabled={isUpdating}
                  sx={{ mr: 1 }}
                >
                  Save
                </Button>
                <Button variant="outlined" onClick={handleEditToggle} color="secondary">
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="contained" onClick={handleEditToggle} color="primary">
                Edit
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          <TextField
            label="Username"
            value={user?.username || ""}
            InputProps={{ readOnly: true, style: { fontWeight: 100 } }}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              "& .MuiInputLabel-root": {
                color: theme.palette.text.primary,
                "&.Mui-focused": {
                  color: theme.palette.primary.main,
                },
              },
            }}
          />
          <TextField
            label="First Name"
            value={isEditing ? editedUser.first_name : (user?.first_name || "")}
            onChange={(e) => handleInputChange("first_name", e.target.value)}
            InputProps={{ 
              readOnly: !isEditing, 
              style: { 
                fontWeight: 100,
                backgroundColor: isEditing ? theme.palette.action.hover : 'transparent'
              } 
            }}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              "& .MuiInputLabel-root": {
                color: theme.palette.text.primary,
                "&.Mui-focused": {
                  color: theme.palette.primary.main,
                },
              },
            }}
          />
          <TextField
            label="Last Name"
            value={isEditing ? editedUser.last_name : (user?.last_name || "")}
            onChange={(e) => handleInputChange("last_name", e.target.value)}
            InputProps={{ 
              readOnly: !isEditing, 
              style: { 
                fontWeight: 100,
                backgroundColor: isEditing ? theme.palette.action.hover : 'transparent'
              } 
            }}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              "& .MuiInputLabel-root": {
                color: theme.palette.text.primary,
                "&.Mui-focused": {
                  color: theme.palette.primary.main,
                },
              },
            }}
          />
          <TextField
            label="Email"
            value={isEditing ? editedUser.email : (user?.email || "")}
            onChange={(e) => handleInputChange("email", e.target.value)}
            InputProps={{ 
              readOnly: !isEditing, 
              style: { 
                fontWeight: 100,
                backgroundColor: isEditing ? theme.palette.action.hover : 'transparent'
              } 
            }}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              "& .MuiInputLabel-root": {
                color: theme.palette.text.primary,
                "&.Mui-focused": {
                  color: theme.palette.primary.main,
                },
              },
            }}
          />
        </Box>

        {/* Password Reset Section */}
        {showPasswordFields && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: "1rem" }}>
              Reset Password
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Enter New Password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.primary,
                    fontWeight: 100,
                    "&.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              />
              <TextField
                label="Re-enter New Password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputLabel-root": {
                    color: theme.palette.text.primary,
                    fontWeight: 100,
                    "&.Mui-focused": {
                      color: theme.palette.primary.main,
                    },
                  },
                }}
              />
              {passwordError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {passwordError}
                </Alert>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {!showPasswordFields ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleResetPasswordClick}
            >
              Reset Password
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handlePasswordSubmit}
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancelPasswordReset}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>

        {resetMessage && (
          <Alert severity={resetMessage.type} sx={{ mt: 2 }}>
            {resetMessage.text}
          </Alert>
        )}
      </Paper>

      {/* Sites Information */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" } }}
      >
        Your Sites
      </Typography>
      {siteDetails.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 3,
          }}
        >
          {siteDetails.map((site) => (
            <Box key={site.id} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <SiteImage
                  src={site.image}
                  alt={site.name || `Site ${site.id}`}
                  sx={{ height: 140 }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
                  >
                    {site.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Latitude: {site.latitude}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Longitude: {site.longitude}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Devices: {site.device_count}
                  </Typography>
                  {site.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {site.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body1" color="text.secondary">
          No sites found.
        </Typography>
      )}
    </Box>
  );
};

export default AccountPage;
